import React, { useRef, useState, useEffect } from "react";
import InitialAvatar from "../InitialAvatar/InitialAvatar";
import "./Header.scss";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import { useNavigate } from "react-router-dom";
import { StyledMenu } from "../../../../Theme/StyledMenu";
import { ReactComponent as Logo } from "../../../../assets/svg/Logo.svg";
import { ReactComponent as Logout } from "../../../../assets/svg/logout.svg";
import { ReactComponent as HamBurger } from "../../../../assets/svg/HamBurger.svg";
import { ReactComponent as OpenHamBurger } from "../../../../assets/svg/OpenHamBurger.svg";
import GettingStartedModal from "../../../UserManagement/GettingStartedModal/GettingStartedModal";
import GettingStarted from "../../SVG/GettingStarted";
import { useAuth } from "../../../../context/AuthProvider";
import { Utility } from "../../../../utils/Utility";
import { useLazySignOutQuery } from "../../../../services/rtkQueryServices/authenticationService";
import { dataStore } from "../../../../constants/dataStore";
import { UserService } from "../../../../services/UserService";
import ChangePassword from "../../../ChangePassword/ChangePassword";
import ConfirmationDialog from "../../ConfirmationDialog/ConfirmationDialog";
import BrandingImage from "../../SVG/BrandingImage";
import { Roles } from "../../../../constants/data/Roles";
import Download from "../../SVG/Download";
import { MedicareBanner } from "../../../UserManagement/ClinicDetails/ClinicUserManagement/MedicareBanner/MedicareBanner";
import { API_CONSTANTS } from "../../../../constants/apiConstants";
import { errorMessages } from "../../../../constants/errorMessages";

export interface HeaderProps {
  userName?: string;
  role?: string;
  profilePic?: string;
  isActive?: boolean;
  seenNotification?: () => void;
  onSearch?: (value: string) => void;
  setIsSideNavOpen: (value: boolean) => void;
}

const Header = (props: HeaderProps) => {
  const { profilePic, isActive } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const [headerShadow, setHeaderShadow] = useState<boolean>(false);
  const profileRef = useRef(null);
  const auth = useAuth();
  const [setSignOut] = useLazySignOutQuery();
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [showChangePasswordModal, setShowChangePasswordModal] =
    useState<boolean>(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const handleDownloadClick = async () => {
    if (isDownloading) return; 
    
    setIsDownloading(true);
    setDownloadError(null);

    try {
      const response = await fetch(`${API_CONSTANTS.EVO_PORTAL_BASE_URL}${API_CONSTANTS.GET_INSTALLER_DOWNLOAD_URL}`, {
        method: "GET",
        headers: {
          "Content-Type": "*/*",
          Accept: "*/*",
        },
        credentials: "include",
      });

      if (response.status === 401) {
        throw new Error(errorMessages.downloadSessionExpired);
      }

      let responseData: any;
      try {
        responseData = await response.json();
      } catch {
        throw new Error(errorMessages.downloadInvalidResponse);
      }

      if (!response.ok) {
        throw new Error(responseData.message || errorMessages.downloadInvalidResponse);
      }
      const secureDownloadUrl = responseData.data;
      if (!secureDownloadUrl) {
        throw new Error("Download URL was empty.");
      }
      const link = document.createElement('a');
      link.href = secureDownloadUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error: any) {
      console.error("Error preparing secure download:", error);
      setDownloadError(error.message);
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    handleResize();
    function handleResize(): void {
      if (window.innerWidth <= 768) setIsMobile(true);
      else setIsMobile(false);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobile]);
  useEffect(() => {
    window.addEventListener("scroll", scrollEvent);
    function scrollEvent(event: Event) {
      if (window.scrollY > 20) {
        setHeaderShadow(true);
      } else {
        setHeaderShadow(false);
      }
    }
    window.addEventListener("keydown", focusSearchInput);
    function focusSearchInput(event: KeyboardEvent): void {
      if (event.ctrlKey && event.code === "KeyF" && inputRef.current) {
        inputRef.current.focus();
      }
    }
    return () => {
      window.removeEventListener("keydown", focusSearchInput);
    };
  });
  const logOut = async () => {
    auth?.logOut();
  };

  const [anchorEl, setAnchorEl] = useState<any>();
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };
  const userRole = auth?.authClaims?.role || "";
  const [openSignOutConfirmation, setOpenSignOutConfirmation] = 
    useState<boolean>(false);
  return (
    <>
      <div className="header-wrapper">
        {process.env.REACT_APP_DISPLAY_MEDICARE_BANNER === "true" && (
          <MedicareBanner />
        )}
        <header className={"main-header " + (headerShadow && "header-shadow")}>
          {(userRole.toUpperCase() === Roles.superUser ||
            userRole.toUpperCase() === Roles.supportUser) &&
            (isActive ? (
              <OpenHamBurger
                onClick={() => {
                  props.setIsSideNavOpen(!isActive);
                }}
                className="cursor-pointer"
              />
            ) : (
              <HamBurger
                onClick={() => {
                  props.setIsSideNavOpen(!isActive);
                }}
                className="cursor-pointer"
              />
            ))}
          <div className="w-100 d-flex align-items-center">
            <div className="cursor-pointer evo-user-management-logo-wrapper ms-3">
              {isMobile ? <BrandingImage width={80} height={50} /> : <Logo />}
            </div>
            <div className="actions">
              <div className="account-action gap-4">
                <Button className="clinic-button download-app d-flex align-items-center"
                  onClick={handleDownloadClick}
                  disabled={isDownloading}
                >
                  <div className="d-flex align-items-center" style={{ cursor: 'pointer', width: '100%' }}>
                    <Download
                      color={"var(--primary-color)"}
                      className="default-download"
                    />
                    <Download
                      color={"var(--white)"}
                      className="hover-download"
                    />
                    <span className="px-2">
                      {isDownloading ? 'Downloading...' : 'Download EVO App'}
                    </span>
                  </div>
                </Button>
                {downloadError && (
                  <div className="text-danger small mt-1 position-absolute" style={{ top: '100%' }}>
                    {downloadError}
                  </div>
                )}
                {userRole === Roles.clinicAdmin && (
                  <span
                    title="Getting started"
                    className="cursor-pointer getting-started-icon"
                    onClick={() => {
                      setOpenModal(true);
                    }}
                  >
                    <GettingStarted />
                  </span>
                )}
                <Button
                  id="demo-customized-button"
                  aria-controls={open ? "demo-customized-menu" : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? "true" : undefined}
                  variant="text"
                  disableElevation
                  onClick={handleClick}
                  className="h-100"
                >
                  <div className="user-profile" ref={profileRef}>
                    <div className="user-profile-control">
                      <InitialAvatar
                        avatar={profilePic}
                        bgColor={"var(--primary-color)"}
                        name={auth?.authClaims?.userName}
                      ></InitialAvatar>
                      {!isMobile && (
                        <div className="d-flex flex-column align-items-baseline">
                          <span
                            className="user-name"
                            title={auth?.authClaims?.userName}
                          >
                            {auth?.authClaims?.userName}
                            <svg
                              width="10"
                              height="7"
                              viewBox="0 0 10 7"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              className="ms-3"
                            >
                              <path
                                d="M8.21973 0.969727L4.99998 4.18948L1.78023 0.969727L0.719727 2.03023L4.99998 6.31048L9.28023 2.03023L8.21973 0.969727Z"
                                fill="rgb(119, 119, 119)"
                              />
                            </svg>
                          </span>
                          <span className="user-name role">
                            {Utility.convertToPascalCase(userRole)}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </Button>
                <StyledMenu
                  id="demo-customized-menu"
                  MenuListProps={{
                    "aria-labelledby": "demo-customized-button",
                  }}
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleClose}
                >
                  {isMobile && (
                    <MenuItem className="border-bottom">
                      <div className="d-flex gap-4">
                        <InitialAvatar
                          avatar={profilePic}
                          bgColor={"var(--primary-color)"}
                          name={auth?.authClaims?.userName}
                        ></InitialAvatar>
                        <div className="d-flex flex-column align-items-baseline">
                          <span
                            className="user-name"
                            title={auth?.authClaims?.userName}
                          >
                            {auth?.authClaims?.userName}
                          </span>
                          <span className="user-name role">
                            {Utility.convertToPascalCase(userRole)}
                          </span>
                        </div>
                      </div>
                    </MenuItem>
                  )}
                  <MenuItem>
                    <div
                      className="d-flex align-items-center w-100 mx-3 gap-2 p-0 profile-wrapper logout-wrapper"
                      onClick={() => {
                        setShowChangePasswordModal(true);
                        handleClose();
                      }}
                    >
                      <span>Change Password</span>
                    </div>
                  </MenuItem>
                  <MenuItem>
                    <div className="d-flex align-items-center w-100 mx-3 gap-2 p-0 profile-wrapper logout-wrapper">
                      <span>
                        <a
                          href={process.env.REACT_APP_SUPPORT_LINK}
                          className="link"
                          target="_blank"
                        >
                          Help & Support
                        </a>
                      </span>
                    </div>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      handleClose();
                      setOpenSignOutConfirmation(true);
                    }}
                    disableRipple
                  >
                    <div className="d-flex align-items-center w-100 mx-3 gap-2 p-0 profile-wrapper logout-wrapper">
                      <Logout />
                      <span>Sign Out</span>
                    </div>
                  </MenuItem>
                </StyledMenu>
              </div>
            </div>
          </div>
        </header>
      </div>
      <GettingStartedModal
        isOpen={openModal}
        onClose={() => {
          setOpenModal(false);
        }}
      />
      <ConfirmationDialog
        open={openSignOutConfirmation}
        onConfirm={() => {
          setSignOut({}).then((res) => {
            if (res.isSuccess) {
              localStorage.removeItem(dataStore.subscriptionId);
              logOut();
            } else if (res.isError) {
              UserService.logOut();
            }
          });
        }}
        contentBody={
          <div>
            <div className="confirmation-header">Confirm sign out?</div>
            <div className="pt-3 confirmation-text">
              You will be signed out from the portal.
            </div>
          </div>
        }
        isAlertConfirmation={true}
        confirmButtonName="Sign Out"
        onCancel={() => {
          setOpenSignOutConfirmation(false);
        }}
        cancelButtonClassName={"cancel-button"}
        confirmButtonClassName="confirm-button"
      />
      <ChangePassword
        isOpen={showChangePasswordModal}
        onClose={() => {
          setShowChangePasswordModal(false);
        }}
      />
    </>
  );
};
export default Header;
